import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createSvgEl, createZoomViewport } from '@learnforge/demo-render';
import { formatNumber } from '@learnforge/demo-math/numeric';
import { createToggleRow } from '@learnforge/demo-kit';
import { createTriangle } from '../render/triangle';
import { createDragHandle } from '../render/drag-handle';
import { createAxes } from '../render/axes';
import { createExactValueBadge } from '../render/exact-value-badge';
import { ratiosFromAngle } from '../trig/ratios';
import { radToDeg, degToRad, snapToSpecialDegree, snapToSpecialRadian } from '../trig/angles';
import { createActionBar } from '../action-bar';
import type { TrigContext } from '../types';
import type { TrigKey } from '../i18n';

const BASE_VIEW = { x: -6, y: -5, width: 12, height: 10 };

// ---------------------------------------------------------------------------
// State — model only (no DOM refs)
// ---------------------------------------------------------------------------

interface State {
  highlighted: 'A' | 'B';
  units: 'deg' | 'rad';
  snap: boolean;
  reveal: { sin: boolean; cos: boolean; tan: boolean };
  legA: number;
  legB: number;
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, TrigKey, TrigContext> {
  return {
    labelKey: 'experiment.right-triangle.label',
    demoId: 'trigonometry',
    advancedKey: 'right-triangle',

    initialState(): State {
      return {
        highlighted: 'A',
        units: 'deg',
        snap: false,
        reveal: { sin: true, cos: true, tan: true },
        legA: 4,
        legB: 3,
      };
    },

    createRender(ctx, host, controls) {
      // ---- SVG stage ----
      const svg = createSvgEl('svg', {
        viewBox: `${BASE_VIEW.x} ${BASE_VIEW.y} ${BASE_VIEW.width} ${BASE_VIEW.height}`,
        preserveAspectRatio: 'xMidYMid meet',
        class: 'tg-svg',
        'font-size': '0.25',
      });
      host.appendChild(svg);

      // Outer <g> flips Y so math coords point up
      const world = createSvgEl('g', { transform: 'scale(1 -1)' });
      svg.appendChild(world);

      const axes = createAxes({
        xDomain: [-6, 6],
        yDomain: [-5, 5],
        i18n: ctx.i18n,
      });
      world.appendChild(axes.group);

      // ---- Triangle geometry ----
      const triangleGroup = createSvgEl('g');
      world.appendChild(triangleGroup);

      const triangle = createTriangle(triangleGroup, {
        vertices: [
          { x: 0, y: 0 },
          { x: 4, y: 0 },
          { x: 0, y: 3 },
        ],
        showSideLabels: ['a', 'b', 'c'],
        i18n: ctx.i18n,
      });

      // ---- Drag handles ----
      const handleGroup = createSvgEl('g');
      world.appendChild(handleGroup);

      // Closure variable: always reflects the last update(state) call.
      // onMove/snap callbacks read this so they see live state without
      // mutating closure scalars.
      let current: State = {
        highlighted: 'A',
        units: 'deg',
        snap: false,
        reveal: { sin: true, cos: true, tan: true },
        legA: 4,
        legB: 3,
      };

      // Snap helper: reads current snap/legA/legB, returns constrained point.
      function snapPosition(p: { x: number; y: number }): { x: number; y: number } {
        if (!current.snap) return p;
        const hyp = Math.hypot(current.legA, current.legB);
        if (hyp < 1e-9) return p;
        const currentAngleDeg = radToDeg(Math.atan2(current.legB, current.legA));
        const snappedDeg = snapToSpecialDegree(currentAngleDeg, 3);
        if (snappedDeg === null) return p;
        const snappedRad = degToRad(snappedDeg);
        const newLegA = Math.max(0.5, hyp * Math.cos(snappedRad));
        const newLegB = Math.max(0.5, hyp * Math.sin(snappedRad));
        if (p.y === 0) return { x: newLegA, y: 0 };
        return { x: 0, y: newLegB };
      }

      const handleA = createDragHandle(handleGroup, {
        cx: 4,
        cy: 0,
        r: 0.25,
        snap: (x) => snapPosition({ x, y: 0 }),
        onMove: ({ x }) => {
          // `x` is already the snap-corrected coordinate (drag handle ran snap first).
          // Derive the paired leg from the same snapped geometry.
          const newLegA = Math.max(0.5, x);
          const hyp = Math.hypot(current.legA, current.legB);
          const newLegB = current.snap
            ? Math.max(0.5, Math.sqrt(Math.max(0, hyp * hyp - newLegA * newLegA)))
            : current.legB;
          controls.setState((s) => ({ ...s, legA: newLegA, legB: newLegB }));
        },
      });

      const handleB = createDragHandle(handleGroup, {
        cx: 0,
        cy: 3,
        r: 0.25,
        snap: (_x, y) => snapPosition({ x: 0, y }),
        onMove: ({ y }) => {
          // `y` is already the snap-corrected coordinate.
          const newLegB = Math.max(0.5, y);
          const hyp = Math.hypot(current.legA, current.legB);
          const newLegA = current.snap
            ? Math.max(0.5, Math.sqrt(Math.max(0, hyp * hyp - newLegB * newLegB)))
            : current.legA;
          controls.setState((s) => ({ ...s, legA: newLegA, legB: newLegB }));
        },
      });

      // ---- Zoom / pan. Mounted after axes + handles: creation fires onChange once. ----
      const viewport = createZoomViewport(svg, {
        base: BASE_VIEW,
        yUp: true,
        fontSize: 0.25,
        onChange: (view) => {
          axes.update({ xDomain: view.xDomain, yDomain: view.yDomain, chromeScale: 1 / view.zoom });
          handleA.setScale(1 / view.zoom);
          handleB.setScale(1 / view.zoom);
        },
      });

      // ---- Panel ----
      // Build panel DOM once. Readout DOM refs captured in closure.

      // --- Highlighted angle picker (A / B) ---
      const angleSection = document.createElement('div');
      angleSection.className = 'tg-panel-section';

      const angleSectionLabel = document.createElement('div');
      angleSectionLabel.className = 'tg-panel-label';
      angleSectionLabel.textContent = 'Angle';
      angleSection.appendChild(angleSectionLabel);

      const anglePicker = document.createElement('div');
      anglePicker.className = 'tg-btn-group';

      const btnA = document.createElement('button');
      btnA.textContent = ctx.i18n.t('rt.angle.a');
      btnA.className = 'tg-btn tg-btn-active';

      const btnB = document.createElement('button');
      btnB.textContent = ctx.i18n.t('rt.angle.b');
      btnB.className = 'tg-btn';

      btnA.addEventListener('click', () => {
        controls.setState((s) => ({ ...s, highlighted: 'A' }));
      });
      btnB.addEventListener('click', () => {
        controls.setState((s) => ({ ...s, highlighted: 'B' }));
      });

      anglePicker.append(btnA, btnB);
      angleSection.appendChild(anglePicker);
      ctx.panel.appendChild(angleSection);

      // --- Units toggle (deg / rad) ---
      const unitsToggle = createToggleRow({
        labelKey: 'rt.units.rad',
        i18n: ctx.i18n,
        checked: false,
        onChange: (v) => {
          controls.setState((s) => ({ ...s, units: v ? 'rad' : 'deg' }));
        },
      });
      ctx.panel.appendChild(unitsToggle.element);

      // --- Angle value readout ---
      const angleReadoutRow = document.createElement('div');
      angleReadoutRow.className = 'tg-readout-row';

      const angleReadoutLabel = document.createElement('span');
      angleReadoutLabel.className = 'tg-panel-label';
      angleReadoutLabel.textContent = ctx.i18n.t('rt.angle.value');
      ctx.i18n.subscribe(() => {
        angleReadoutLabel.textContent = ctx.i18n.t('rt.angle.value');
      });
      angleReadoutRow.appendChild(angleReadoutLabel);

      const angleValueEl = document.createElement('span');
      angleValueEl.className = 'tg-ratio-value';
      angleReadoutRow.appendChild(angleValueEl);

      ctx.panel.appendChild(angleReadoutRow);

      // --- Readout rows: sin / cos / tan ---
      function makeReadoutRow(
        ratioKey: 'rt.show.sin' | 'rt.show.cos' | 'rt.show.tan',
        revealKey: 'sin' | 'cos' | 'tan',
      ) {
        const row = document.createElement('div');
        row.className = 'tg-readout-row';

        const toggle = createToggleRow({
          labelKey: ratioKey,
          i18n: ctx.i18n,
          checked: true,
          onChange: (v) => {
            controls.setState((s) => ({
              ...s,
              reveal: { ...s.reveal, [revealKey]: v },
            }));
          },
        });
        row.appendChild(toggle.element);

        const valueEl = document.createElement('span');
        valueEl.className = 'tg-ratio-value';
        valueEl.style.display = '';
        row.appendChild(valueEl);

        const badge = createExactValueBadge();
        badge.element.style.display = '';
        row.appendChild(badge.element);

        ctx.panel.appendChild(row);

        return { row, valueEl, badge };
      }

      const sinRow = makeReadoutRow('rt.show.sin', 'sin');
      const cosRow = makeReadoutRow('rt.show.cos', 'cos');
      const tanRow = makeReadoutRow('rt.show.tan', 'tan');

      // ---- Action bar ----
      const actionBarHandle = createActionBar(ctx.actionBar, {
        buttons: [
          {
            id: 'snap',
            labelKey: 'rt.snap',
            onClick: () => {
              controls.setState((s) => {
                const nextSnap = !s.snap;
                return { ...s, snap: nextSnap };
              });
            },
          },
          { id: 'view', labelKey: 'view.reset', onClick: () => viewport.reset() },
        ],
        i18n: ctx.i18n,
      });
      actionBarHandle.setActive('snap', false);

      // ---------------------------------------------------------------------------
      // update — reconcile view from state
      // ---------------------------------------------------------------------------
      function update(state: State): void {
        // First: refresh the live-state reference used by drag callbacks.
        current = state;

        const { legA, legB, highlighted, units, snap, reveal } = state;

        // Reposition triangle + handles
        triangle.update({
          vertices: [
            { x: 0, y: 0 },
            { x: legA, y: 0 },
            { x: 0, y: legB },
          ],
        });
        handleA.setPosition(legA, 0);
        handleB.setPosition(0, legB);

        // Snap button active state
        actionBarHandle.setActive('snap', snap);

        // A/B button highlight
        if (highlighted === 'A') {
          btnA.className = 'tg-btn tg-btn-active';
          btnB.className = 'tg-btn';
        } else {
          btnA.className = 'tg-btn';
          btnB.className = 'tg-btn tg-btn-active';
        }

        // Compute trig values
        let sinVal: number;
        let cosVal: number;
        let tanVal: number;
        let angleRad: number;

        if (highlighted === 'A') {
          angleRad = Math.atan2(legB, legA);
          const r = ratiosFromAngle(angleRad);
          sinVal = r.sin;
          cosVal = r.cos;
          tanVal = r.tan;
        } else {
          // Angle B = π/2 − A
          angleRad = Math.atan2(legA, legB);
          const r = ratiosFromAngle(angleRad);
          sinVal = r.sin;
          cosVal = r.cos;
          tanVal = r.tan;
        }

        function formatAngle(a: number): string {
          if (units === 'deg') {
            return `${radToDeg(a).toFixed(2)}°`;
          }
          const special = snapToSpecialRadian(a);
          if (special) {
            if (special.num === 0) return '0';
            if (special.num === special.den) return 'π';
            if (special.den === 1) return `${special.num}π`;
            if (special.num === 1) return `π/${special.den}`;
            return `${special.num}π/${special.den}`;
          }
          return a.toFixed(4);
        }

        angleValueEl.textContent = formatAngle(angleRad);

        // Sin readout
        sinRow.valueEl.style.display = reveal.sin ? '' : 'none';
        sinRow.badge.element.style.display = reveal.sin ? '' : 'none';
        if (reveal.sin) {
          sinRow.valueEl.textContent = formatNumber(sinVal, { decimals: 4, nonFinite: '∞' });
          sinRow.badge.setValue(sinVal);
        }

        // Cos readout
        cosRow.valueEl.style.display = reveal.cos ? '' : 'none';
        cosRow.badge.element.style.display = reveal.cos ? '' : 'none';
        if (reveal.cos) {
          cosRow.valueEl.textContent = formatNumber(cosVal, { decimals: 4, nonFinite: '∞' });
          cosRow.badge.setValue(cosVal);
        }

        // Tan readout
        tanRow.valueEl.style.display = reveal.tan ? '' : 'none';
        tanRow.badge.element.style.display = reveal.tan ? '' : 'none';
        if (reveal.tan) {
          tanRow.valueEl.textContent = formatNumber(tanVal, { decimals: 4, nonFinite: '∞' });
          tanRow.badge.setValue(tanVal);
        }
      }

      return {
        update,
        dispose() {
          handleA.destroy();
          handleB.destroy();
          triangle.destroy();
          axes.destroy();
          sinRow.badge.destroy();
          cosRow.badge.destroy();
          tanRow.badge.destroy();
          actionBarHandle.destroy();
          viewport.dispose();
          svg.remove();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Alias for the existing index.ts + test imports (behaviorally identical).
export const mountRightTriangle = experiment.mount.bind(experiment);
